import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import {
  AbsoluteFill,
  ApolloError,
  Divider,
  Heading,
  HStack,
  LoadingIndicator,
  NavPersistedScrollPane as ScrollPane,
  SimpleTabView,
  Spacer,
  Tab,
  VStack,
} from '../../core-ui';
import { Button, useNavigation, useNavPersistedState } from '../Navigation';
import { useSeason } from '../SeasonState';

import EmailListView from './EmailListView';
import { SenderAddressRequired } from './SenderAddressRequired';
import { GET_EMAILS_AND_ADDRESSES } from './graphql';
import type {
  GetEmailsAndAddresses,
  GetEmailsAndAddressesVariables,
} from './__generated__/GetEmailsAndAddresses';

export default function EmailListPage() {
  const { navigate } = useNavigation();
  const { season } = useSeason();
  const seasonId = season?.id ?? '';
  let [tabIndex, setTabIndex] = useNavPersistedState('email-list-tab-view', 0);

  const { data, error } = useQuery<
    GetEmailsAndAddresses,
    GetEmailsAndAddressesVariables
  >(GET_EMAILS_AND_ADDRESSES, {
    variables: { seasonId },
    fetchPolicy: 'cache-and-network',
  });

  const emailsDraft = useMemo(() => {
    const emails = data?.season?.emails ?? [];
    return emails.filter((email) => email.status === 'DRAFT');
  }, [data]);

  const emailsOutbox = useMemo(() => {
    const emails = data?.season?.emails ?? [];
    return emails.filter(
      (email) => email.status === 'PENDING' || email.status === 'SENDING',
    );
  }, [data]);

  const emailsSent = useMemo(() => {
    const emails = data?.season?.emails ?? [];
    return emails.filter((email) => email.status === 'SENT');
  }, [data]);

  if (error) {
    return <ApolloError error={error} />;
  }

  if (!data) {
    return <LoadingIndicator />;
  }

  const senderAddresses = data.myAccount.senderAddresses;
  const verifiedSenderAddresses = senderAddresses.filter(
    (address) => address.forwardTo.isVerified,
  );
  if (verifiedSenderAddresses.length === 0) {
    return <SenderAddressRequired />;
  }

  return (
    <VStack px={10} pt={10} flex={1} minH={0} alignItems="stretch" spacing={2}>
      <HStack>
        <Heading as="h2" size="lg">
          {t('Emails')}
        </Heading>
        <Spacer />
        <Button to={['/mailer/emails/new']} variant="primary">
          {t('Create New Email')}
        </Button>
      </HStack>
      <Divider borderColor="gray.200" />
      <SimpleTabView index={tabIndex} onChange={(index) => setTabIndex(index)}>
        <Tab
          title={t('Drafts ({numOfDrafts})', {
            numOfDrafts: emailsDraft.length,
          })}
          px={0}
        >
          <AbsoluteFill>
            <ScrollPane storeKey="email-list-1">
              <EmailListView
                storeKey="email-list-1"
                data={emailsDraft}
                onEmailClick={(email) => {
                  navigate(`/mailer/emails/:id/edit`, { id: email.id });
                }}
                onEmailDuplicated={() => {
                  setTabIndex(0);
                }}
              />
            </ScrollPane>
          </AbsoluteFill>
        </Tab>
        <Tab
          title={t('Outbox ({numOfEmails})', {
            numOfEmails: emailsOutbox.length,
          })}
          px={0}
        >
          <AbsoluteFill>
            <ScrollPane storeKey="email-list-2">
              <EmailListView
                storeKey="email-list-2"
                data={emailsOutbox}
                onEmailClick={(email) => {
                  let jobId = email.job;
                  if (jobId) {
                    if (email.status === 'PENDING') {
                      navigate('/mailer/jobs/:id/preview', { id: jobId });
                    } else {
                      navigate('/mailer/jobs/:id/progress', { id: jobId });
                    }
                  }
                }}
                onEmailDuplicated={() => {
                  setTabIndex(0);
                }}
                showRecipientCount
              />
            </ScrollPane>
          </AbsoluteFill>
        </Tab>
        <Tab
          title={t('Sent ({numOfSent})', { numOfSent: emailsSent.length })}
          px={0}
        >
          <AbsoluteFill>
            <ScrollPane storeKey="email-list-3">
              <EmailListView
                storeKey="email-list-3"
                data={emailsSent}
                onEmailClick={(email) => {
                  navigate('/mailer/jobs/:id/summary', {
                    id: email.job ?? '',
                  });
                }}
                onEmailDuplicated={() => {
                  setTabIndex(0);
                }}
                showRecipientCount
              />
            </ScrollPane>
          </AbsoluteFill>
        </Tab>
      </SimpleTabView>
    </VStack>
  );
}
